import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getLastResults, getDetailResults } from '../../../../apiservices/CasionApi';
import RefResultModal from '../../../casinoComponents/refResultModal';
import DragonTigerResultBody from '../../../casinoComponents/refResultModal/DragonTigerResultBody';
import { formatRefMatchTime } from '../../../../utils/refMatchTime';

// Fallback modal titles (the modal shows the round's `ename` + " Result")
const TITLES = {
  dt20: '20-20 Dragon Tiger',
  pdt20: 'Premium 20-20 Dragon Tiger',
  dt202: '20-20 Dragon Tiger 2',
};

/**
 * Last Result items for 20-20 Dragon Tiger (dt20 / pdt20 / dt202) in the reference markup —
 * rendered inside `.casino-video-last-results`:
 *   win 1 → <span class="resulta">D</span>, 2 → <span class="resultb">T</span>, 3 → <span class="resulttie">T</span>
 * Click → full-page .loader-overlay while the round detail loads, then the reference result modal.
 */
const ResultDragonTiger = ({ data = [], gameType }) => {
  const apiType = (gameType || 'dt20').toLowerCase().trim();
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(apiType);
        if (response?.data?.data?.res) {
          setLastResults(response.data.data.res.map((item) => ({ mid: item.mid, win: String(item.win) })));
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [apiType]);

  const handleResultClick = async (item) => {
    if (!item?.mid) return;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, item.mid.toString());
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (t1) {
        setDetailResult(t1);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching detail results:', error);
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDetailResult(null);
  };

  // API results; before they arrive, the feed's lrs ("D"/"T" or {mid, win}) as a fallback
  const items = lastResults.length > 0
    ? lastResults
    : (Array.isArray(data) ? data : []).map((r) =>
        typeof r === 'object' && r
          ? { mid: r.mid, win: String(r.win) }
          : { mid: null, win: r === 'D' ? '1' : r === 'T' ? '2' : String(r) }
      );

  return (
    <>
      {items.map((item, index) => {
        const cls = item.win === '1' ? 'resulta' : item.win === '2' ? 'resultb' : item.win === '3' ? 'resulttie' : undefined;
        return (
          <span key={item.mid || index} className={cls} onClick={() => handleResultClick(item)}>
            {item.win === '1' ? 'D' : item.win === '2' || item.win === '3' ? 'T' : ''}
          </span>
        );
      })}
      {loading && createPortal(<div className="loader-overlay" />, document.querySelector('.page-content') || document.body)}
      <RefResultModal
        show={showModal && !loading && !!detailResult}
        onHide={handleCloseModal}
        title={`${detailResult?.ename || TITLES[apiType] || TITLES.dt20} Result`}
        roundId={detailResult?.rid}
        matchTime={formatRefMatchTime(detailResult?.mtime)}
      >
        <DragonTigerResultBody t1={detailResult} />
      </RefResultModal>
    </>
  );
};

export default ResultDragonTiger;
